/**
 * The Deepwake Codex — the history of the Reach, as the Reach tells it.
 * Entries unlock nothing; they exist to be read. All original.
 */

export interface CodexEntry {
  id: string;
  title: string;
  category: 'history' | 'factions' | 'places' | 'ships' | 'practices';
  body: string;
}

export const CODEX: CodexEntry[] = [
  {
    id: 'lattice',
    title: 'The Lattice',
    category: 'history',
    body: `Nobody built the gates in living memory, and nobody alive can say who did. When the first slow-ships found a Warden gate drifting at the edge of their home system — a ring of black segments that drank light and hummed at the edge of hearing — humanity did what humanity does: it threw a probe through, then a volunteer, then an economy.

The network they unlocked came to be called the Lattice. Four hundred years of expansion followed. A thousand systems, ten thousand habitats, a species stretched thin and glittering across the arm of a galaxy it never learned to cross on its own.

We rode roads we could not repair, through doors we could not lock. The Provenance archivists call this period the Borrowing. It is not a compliment.`,
  },
  {
    id: 'stilling',
    title: 'The Stilling',
    category: 'history',
    body: `It happened between one breath and the next, four generations ago. Every gate in the Lattice went dark in the same instant — not failing, the physicists insist, but *closing*, the way an eye closes.

Ships in transit never arrived. Systems that grew food lost the systems that grew people. The webs of supply that made a thousand worlds livable snapped in a day, and most of what humanity had been simply — stopped.

No cause was ever found. The Vigil says the Wardens turned their faces away. The Combine's official position is that the event was "an infrastructural interruption of unknown duration." The Unbound say the universe finally told the truth: nobody is coming, and nothing is owed.`,
  },
  {
    id: 'reach',
    title: 'The Reach',
    category: 'history',
    body: `The Reach is a cluster of two dozen systems that survived the Stilling out of spite. Its people rebuilt slow lanes between close stars, burned dark decades in stations built for ten times their number, and kept ledgers of everything they had lost so that the next generation would know what to be angry about.

Then, thirty-one years ago, the gate at Vigilkeep opened its eye.

One by one — without pattern, without warning — the gates of the Reach have been waking since. Each waking redraws every map and every treaty. Nobody knows which gate wakes next, or whether the Stilling will ever run in reverse. The people who fly anyway are called Wakers. You are one of them.`,
  },
  {
    id: 'wardens',
    title: 'The Wardens',
    category: 'history',
    body: `The builders of the gates left no bodies, no cities, no language — only the gates, and the fragments. A Warden gate does not corrode, does not respond, and does not open for anyone who tries to force it; several fleets across history are thinner for having tried.

Gate fragments — segments sheared off by ancient violence nobody witnessed — are the most valuable objects in the Reach. They hum in warp. Reliquary-pattern cruisers grown around a fragment report dreams among the crew: a shoreline, a bell, a door left open. The Vigil pays well for recordings, and refuses to say what it is listening for.`,
  },
  {
    id: 'provenance-combine',
    title: 'The Provenance Combine',
    category: 'factions',
    body: `The Combine kept the lights on. When the Stilling cut the Reach loose, it was the foundry clans of Provenance who rationed, indexed, salvaged, and — their word — *provenanced* every working machine in the cluster. Four generations later that discipline has hardened into an industrial oligarchy that owns most of Hearthlight and holds a mortgage on the rest.

The Combine's law is contract law. Its cruelty is fine print. Its gift, which even its enemies concede, is that things built to Combine standard work, and keep working, and can be repaired by the grandchildren of the people who built them.`,
  },
  {
    id: 'tidemark-compact',
    title: 'The Tidemark Compact',
    category: 'factions',
    body: `Some crews survived the Stilling because they never trusted a gate to begin with. The nomad fleets — haulers, tramp traders, comet farmers — banded into the Compact: part navy, part market, part extended family with excellent bookkeeping.

The Compact runs the Verge the way a tide runs a shore: it arrives, it trades, it leaves things better arranged than it found them, and it drowns people who mistake its patience for weakness. Every Compact ship carries its ledger openly. Debts are sacred. Scams are legal. The difference, they will tell you, is that a scam is written down.`,
  },
  {
    id: 'vigil',
    title: 'The Vigil',
    category: 'factions',
    body: `For four dark generations, the order at Vigilkeep kept watch over a dead gate. They cleaned it. They lit it. They sang to it on the old calendar, in shifts, so that it was never once unwatched. The Reach thought them harmless — grief wearing a uniform.

Then their gate woke first.

The Vigil does not claim credit. It claims *confirmation*. Its escorts patrol the gate lanes, its tithes fund the shrines, and its Lances are very fast and very patient. What the Vigil actually believes about the Wardens is layered in vows; what it does is simpler: gates stay open where the Vigil keeps watch, and everyone has quietly decided not to test the correlation.`,
  },
  {
    id: 'unbound',
    title: 'The Unbound',
    category: 'factions',
    body: `Out past the last patrol, in the systems the Reach lost twice, live the crews who concluded that the Stilling was not a catastrophe but a verdict. No ledgers, no tithes, no fine print. The Unbound take what drifts past and defend what they take.

It would be comfortable to call them pirates, and most people do, and most of them are. But the Deep keeps its own history: it was Unbound salvage crews who first cut into dead gates, Unbound shipwrights who first grew a hull around a fragment, and Unbound charts that every "civilized" navigator quietly copies. The Reach's future keeps getting invented by the people it exiled.`,
  },
  {
    id: 'hearthlight',
    title: 'Hearthlight',
    category: 'places',
    body: `The core of the Reach: eight systems under Combine contract and Watch protection, where violence against another Waker's hull is met with immediate, overwhelming, and impeccably documented response. Hearthlight is safe the way a factory floor is safe — because everything here is load-bearing.

New Wakers shellcast at Provenance. Learn the trade lanes. Read the fine print.`,
  },
  {
    id: 'verge',
    title: 'The Verge',
    category: 'places',
    body: `Between the hearth and the dark: eight systems of Compact anchorages, contested drifts, and the best margins in the Reach. The Watch does not patrol here; consequences are negotiated privately. Ships that duel in the Verge do so flagged and witnessed — and ships that gank unflagged learn why the Compact's memory is longer than its patience.

Richer ore than Hearthlight. Better prices. Worse odds.`,
  },
  {
    id: 'deep',
    title: 'The Deep',
    category: 'places',
    body: `The eight lost systems. Unbound harbors, dead habitats, gates that woke with nobody left to notice, and drifts of stillore that formed in the decades nothing moved out here. No law, no flags, no Watch. The finest ore in the Reach sleeps in the Deep, and the Unbound sleep next to it with one eye open.

Everything you fly into the Deep, you have already decided you can lose.`,
  },
  {
    id: 'shellcasting',
    title: 'Shellcasting',
    category: 'practices',
    body: `A Waker's ship core is stasis-era technology: a sealed mind-and-record vault that survives hull loss with its captain's pattern intact. When your hull dies, the core rides the wreck-pulse home and the foundry prints you a new shell around it.

You lose the hull. You lose the cargo. You keep the self, the skills, and the debts. The Combine considers this an elegant insurance model. The Vigil considers it a sacrament. The Unbound consider it the one piece of Warden-adjacent tech that finally made dying affordable.`,
  },
  {
    id: 'marks',
    title: 'Marks',
    category: 'practices',
    body: `The currency of the Reach is the Mark: a cryptographic bearer note denominated, by four-generation-old convention, in foundry time. One Mark was once one second of a licensed foundry line. The foundries have improved; the notation survives.

Marks are minted by work and burned by fees. Nobody in the Reach will sell you progress — hulls must be built, ore must be pulled, mastery must be flown. The Combine tried scrip once. The Compact's response is studied in economics texts, under "deterrence."`,
  },
  {
    id: 'ores',
    title: 'The Four Ores',
    category: 'practices',
    body: `CINDRAL — hearth-rock, the workhorse. Refines to ferrite for hullplate. Every station in the Reach eats cindral and is hungry.

VEILSPAR — glassy, light-bending, found where solar wind polishes a drift. Rich in lumen, the substrate of optics, capacitors, and everything that thinks.

TIDEGOLD — the Compact named it, the Compact hoards it. Refines to wakemetal, the alloy that holds a warp field's shoulder.

STILLORE — forms only in space that has held perfectly still, which after the Stilling means the Deep. Threaded with gatesong: the alloy that hums. The Vigil tithes it, the Reliquary drinks it, and nobody mines it twice without an escort.`,
  },
  {
    id: 'destiny-flying',
    title: 'On Flying',
    category: 'ships',
    body: `A hull in space is a decision, not a joystick. You tell the core where to be — approach, orbit, hold range, align, warp — and the core flies the damped curve that physics allows. A heavy hull answers slowly. A light hull dances.

Gunnery in the Reach is geometry: a turret tracks; transversal defeats it. Fly across a rail's face and it will part your hair and miss; fly straight at it and it will not miss twice. Watch your tracking quality. Fly the angle, not the trigger.

And mind your heat. Every cycle you demand past the design line is borrowed from the module's future. It will pay you back mid-fight, at the worst moment, with interest.`,
  },
];

export const CODEX_BY_CATEGORY = ['history', 'factions', 'places', 'ships', 'practices'].map((c) => ({
  category: c,
  entries: CODEX.filter((e) => e.category === c),
}));

/** First-flight checklist: the fifteen-minute on-ramp. Client-tracked. */
export const FIRST_STEPS: { id: string; label: string }[] = [
  { id: 'undock', label: 'Undock from the Foundry' },
  { id: 'fly', label: 'Approach or orbit anything' },
  { id: 'warp', label: 'Warp to a Drift' },
  { id: 'lock', label: 'Lock an ore shard' },
  { id: 'mine', label: 'Pull ore with the Maw Cutter (ride the resonance)' },
  { id: 'dock', label: 'Dock and move ore to your hangar' },
  { id: 'refine', label: 'Refine a batch of 100' },
  { id: 'build', label: 'Build anything at the foundry' },
  { id: 'mission', label: 'Accept a mission from the board' },
  { id: 'gate', label: 'Jump a Warden gate' },
];
